/**
 * Types + bucketing for the manager Team page: one row per direct report,
 * guidance across a six-month window, and logged time split into the columns a
 * manager actually reads.
 *
 * The two feeds behind this are `enable-team-guidance` and `enable-team-time`
 * (see `docs/nos-gateway-endpoints.md`). Both take a comma-separated list of
 * PersonIDs so a whole team costs one call each rather than one per report.
 */

import {
  currentMonthKey,
  monthWindow,
  previousMonthKey,
  UNDER_GUIDED_PERCENT,
} from "@/lib/persons/project-guidance";
import type { TeamAttentionEntry } from "@/lib/persons/team-attention";

/** Months of history shown before the current month. */
const TEAM_MONTHS_BACK = 3;
/**
 * Months of plan shown after the current month.
 *
 * Symmetric with the history so the guided grid and the logged-period picker
 * offer the same months; the gateway feeds already reach three months forward.
 */
const TEAM_MONTHS_FORWARD = 3;

/** The five columns logged time is split across. */
export type TimeBucket = "billable" | "nonBillable" | "pto" | "sick" | "misc";

export const TIME_BUCKETS: TimeBucket[] = ["billable", "nonBillable", "pto", "sick", "misc"];

export const TIME_BUCKET_LABELS: Record<TimeBucket, string> = {
  billable: "Billable",
  nonBillable: "Non-billable",
  pto: "PTO",
  sick: "Sick",
  misc: "Other leave",
};

/**
 * NOS `TimeEntryType` → column, for the types that are time OFF rather than
 * work. PTO folds in public holidays; "Other leave" is the catch-all NOS uses
 * for jury duty, parental leave and bereavement, which it records as
 * Exceptional / Unpaid Time Off rather than as distinct types — which is why
 * the column carries a hover breakdown instead of splitting further.
 */
const TIME_OFF_BUCKETS: Record<string, TimeBucket> = {
  "General PTO": "pto",
  "Observed Paid Holiday": "pto",
  "Sick PTO": "sick",
  "Exceptional Time Off": "misc",
  "Unpaid Time Off": "misc",
};

/**
 * Which column an entry belongs in.
 *
 * Time off is identified by TYPE and worked time is split by the `IsBillable`
 * FLAG — never the other way round. The two disagree in NOS: 5,754 Internal
 * rows are flagged billable and 63 Client Billable rows are not, so bucketing
 * work by type would misreport utilisation, and bucketing leave by the flag
 * would scatter it across the work columns.
 */
export function bucketFor(timeEntryType: string, isBillable: boolean): TimeBucket {
  return TIME_OFF_BUCKETS[timeEntryType] ?? (isBillable ? "billable" : "nonBillable");
}

/** One NOS time-entry type and the hours in it, for a column's hover breakdown. */
export interface TimeBreakdownEntry {
  label: string;
  hours: number;
}

/** Logged hours over the window, split into columns, each with its make-up. */
export interface TimeTotals {
  billable: number;
  nonBillable: number;
  pto: number;
  sick: number;
  misc: number;
  /** Constituent NOS types per column, hours descending. Drives the hover. */
  breakdown: Record<TimeBucket, TimeBreakdownEntry[]>;
}

/** One month of guidance for one report. */
export interface TeamGuidanceMonth {
  /** Calendar month key, `YYYY-MM`. */
  month: string;
  /** Total guided hours across the person's assignments. Null means unguided. */
  guidedHours: number | null;
  /** The person's own capacity that month. */
  capacityHours: number | null;
  /** Guided share of capacity, 0–100. Null when either side is missing. */
  allocationPercent: number | null;
}

/** One direct report's row on the Team page. */
export interface TeamMemberSummary {
  personId: string;
  name: string;
  title: string | null;
  avatarUrl: string | null;
  /**
   * How the person is placed and engaged, as IDs. What the company view's
   * filters match on — resolved to labels by the filter controls themselves,
   * which already read `/api/options`.
   *
   * Present on BOTH scopes even though only the company view filters: the two
   * rosters are deliberately interchangeable, and a field only one of them
   * carried would be the first crack in that.
   */
  employmentType: string | null;
  departmentId: string | null;
  /** Derived from the department — a person has no pillar of their own. */
  pillarId: string | null;
  roleTeamId: string | null;
  /** Every job role they hold; empty when they hold none. */
  roleIds: string[];
  /** Earliest month first, one entry per window month, gaps included. */
  months: TeamGuidanceMonth[];
  /**
   * Logged hours PER MONTH (`YYYY-MM` → columns), leave included. A month with
   * no rows is absent, not zero.
   *
   * Deliberately not a window-wide total any more. Summed across six months one
   * row blended four-month-old billable hours with public holidays the person
   * had not taken yet, under a heading that said "Logged" — a real report read
   * "Billable 9.5, PTO 48, Billable % 100" on exactly that basis.
   */
  timeByMonth: Record<string, TimeTotals>;
}

/**
 * Whose figures the report is showing.
 *
 * `team` — the viewer's own direct reports, the default and the only thing a
 * manager gets. `company` — every active person, offered to admins only
 * (WI-307): the report is the same question asked of the whole business, and
 * an admin's own reports are rarely where the problem is.
 */
export type TeamScope = "team" | "company";

export interface TeamSummary {
  /**
   * Which roster this payload holds. Echoed back by the server so the page
   * labels what it actually received rather than what it asked for — the two
   * differ for a moment on every scope switch, and for good after a refused
   * request.
   */
  scope: TeamScope;
  /** Window months, earliest first (`YYYY-MM`). */
  window: string[];
  members: TeamMemberSummary[];
  /**
   * The reports needing attention and why, worst first. Computed server-side
   * on purpose: the pending-leave feed it reads carries a free-text reason
   * that is admin-only, so only the flags cross the wire — never the requests.
   *
   * `null` means the figures could not be checked (no gateway), which the page
   * must say rather than render as an empty, falsely-clean block.
   */
  attention: TeamAttentionEntry[] | null;
  /**
   * Whether the pending-leave feed answered. False means the leave flags are
   * MISSING rather than empty, which the block says out loud — an unapproved
   * request going unmentioned because a call failed would read as "all clear".
   */
  leaveKnown: boolean;
  /**
   * False when the server has no gateway credentials. The page then lists the
   * reports without any NOS figures rather than showing an error — an
   * environment without gateway access is expected, not broken.
   */
  connected: boolean;
}

/** An empty column set, used for a report NOS has no time rows for. */
export function emptyTimeTotals(): TimeTotals {
  return {
    billable: 0,
    nonBillable: 0,
    pto: 0,
    sick: 0,
    misc: 0,
    breakdown: { billable: [], nonBillable: [], pto: [], sick: [], misc: [] },
  };
}

/**
 * The six month keys the page covers: `TEAM_MONTHS_BACK` before the current
 * month, the current month, then `TEAM_MONTHS_FORWARD` after. Derived from the
 * clock rather than hard-coded so the window never reads stale.
 */
export function teamWindowMonths(from: Date = new Date()): string[] {
  return monthWindow(TEAM_MONTHS_BACK, TEAM_MONTHS_FORWARD, from);
}

/** Total logged hours across every column. */
export function totalLogged(time: TimeTotals): number {
  return time.billable + time.nonBillable + time.pto + time.sick + time.misc;
}

/**
 * Billable share of WORKED time (billable + non-billable), 0–100. Leave is
 * excluded from the denominator: a person on holiday for half the month has
 * not become less billable, and counting it would make time off look like
 * under-performance.
 */
export function billableSharePercent(time: TimeTotals): number | null {
  const worked = time.billable + time.nonBillable;
  if (worked <= 0) return null;
  return Math.round((time.billable / worked) * 100);
}

/** Total hours logged in one month, leave included; 0 when NOS has no rows. */
export function loggedInMonth(member: TeamMemberSummary, month: string): number {
  const totals = member.timeByMonth[month];
  return totals ? totalLogged(totals) : 0;
}

/**
 * Whether a period is still to come, so its hours are BOOKED rather than logged.
 *
 * Every window month is selectable — the original defect was six months blended
 * under a heading that named no period, not the presence of forward rows. With
 * the month stated, October's holiday shown as October is honest. But the feed's
 * forward rows are booked public holidays and PTO (never work), so the columns
 * must stop calling them logged, which is what this drives.
 */
export function isBookedPeriod(month: string, from: Date = new Date()): boolean {
  return month > currentMonthKey(from);
}

/**
 * The month the logged columns open on: the CURRENT month.
 *
 * Matt's call, and it also lands the guided highlight on the month a manager
 * expects to be looking at. Note the current month is half-filled by
 * definition, and the "didn't log their time" flag still judges the last
 * COMPLETE month — the two deliberately differ, which is safe only because
 * every flag names its own month in the sentence ("Logged no time in August").
 * The fallback is the last complete month, for a window that somehow excludes
 * the current one.
 */
export function defaultLoggedMonth(window: string[], from: Date = new Date()): string {
  const current = currentMonthKey(from);
  const previous = previousMonthKey(current);
  if (window.includes(current)) return current;
  if (window.includes(previous)) return previous;
  return window[window.length - 1] ?? current;
}

/**
 * Whether a month is guided below the threshold, treating "no guidance set" as
 * under-guided — an unguided month is the strongest version of the signal, not
 * an absence of one. Past months are excluded by the caller; they are history,
 * and flagging them would light up every row permanently.
 */
export function isUnderGuided(month: TeamGuidanceMonth): boolean {
  return (month.allocationPercent ?? 0) < UNDER_GUIDED_PERCENT;
}
